import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';

import ArticleIcon from '@mui/icons-material/Article';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import EmailIcon from '@mui/icons-material/Email';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FinePrintIcon from '@mui/icons-material/ManageSearch';
import SubjectIcon from '@mui/icons-material/Subject';
import SyncIcon from '@mui/icons-material/Sync';
import TuneIcon from '@mui/icons-material/Tune';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import AIGeneratorButton from '~/components/AIGenerator/AIGenerateButton';
import { LUX_PLUS_FINEPRINT_CONTENT_FLASH } from '~/components/Common/Forms/OfferForm/constants/fineprint';
import { RichTextEditor } from '~/components/Common/Forms/fields';
import MarkdownEditor from '~/components/Common/MarkdownEditor';

import { OFFER_FACING_NAMES } from '~/consts/offerTypes';

import { useSyncPropertyFacilities } from '~/hooks/apis/accommodation/useSyncPropertyFacilities';
import { useGetOfferTemplates } from '~/hooks/apis/offers/useGetOfferTemplates';

import { noop } from '~/utils/noop';

import OfferTemplateModal from '../../../../Edit/OfferTemplateModal';

interface Props {
  offerAdditionalDescription?: string;
  offerDescription?: string;
  offerFacilities?: string;
  offerFinePrint?: string;
  offerGettingThere?: string;
  offerHighlights?: string;
  offerName?: string;
  offerPreheader?: string;
  offerSaleUnit?: string;
  offerSlug?: string;
  offerStatus?: string;
  offerSubjectLine?: string;
  offerType?: string;
  propertyId?: string;
}

interface SummaryBodyFormValues {
  facilities: string;
  fine_print: string;
  getting_there: string;
  highlights: string;
}

type TemplateType = 'fine_print' | 'location';

const OFFER_STATUS_OPTIONS = ['draft', 'content-approved', 'ad-feed-only', 'hidden'] as const;
const HEADLINE_TARGET_COPY = 'target 80-140';
const SALE_UNIT_HELPER_TEXT = 'Max 6 chars (room, boat, villa...)';
const ADDITIONAL_DESCRIPTION_PLACEHOLDER =
  'Optional supplementary copy used in extended merchandising surfaces (e.g. category landing pages, second-screen EDM blocks).';
const ADDITIONAL_DESCRIPTION_HELPER_TEXT = 'Optional — appears below the main description on long-form surfaces.';
const EMPTY_HIGHLIGHTS_EDITOR_MIN_HEIGHT = 96;
const POPULATED_HIGHLIGHTS_EDITOR_MIN_HEIGHT = 260;
const HTML_CONTENT_TAG_PATTERN = /<(img|br)\b/i;
const HTML_TAG_PATTERN = /<[^>]*>/g;
const AI_BUTTON_SX = {
  m: 0,
  maxWidth: 'none',
  p: 0,
  '&.MuiContainer-root': {
    px: 0,
  },
  '& .MuiGridLegacy-container': {
    m: 0,
    width: 'auto',
  },
  '& .MuiGridLegacy-item': {
    p: 0,
  },
};
const ACCORDION_SX = {
  bgcolor: 'background.paper',
  borderRadius: 1,
  boxShadow: 1,
  overflow: 'hidden',
  '&:before': {
    display: 'none',
  },
};
const ACCORDION_SUMMARY_SX = {
  minHeight: 64,
  px: 3,
  '& .MuiAccordionSummary-content': {
    my: 2,
  },
};
const ACCORDION_DETAILS_SX = {
  bgcolor: 'grey.50',
  borderTop: 1,
  borderColor: 'divider',
  p: 3,
};

function hasRichTextContent(value: string) {
  return (
    HTML_CONTENT_TAG_PATTERN.test(value) ||
    value
      .replace(HTML_TAG_PATTERN, '')
      .replace(/&nbsp;/g, ' ')
      .trim().length > 0
  );
}

export default function DefaultContent({
  offerAdditionalDescription,
  offerDescription,
  offerFacilities,
  offerFinePrint,
  offerGettingThere,
  offerHighlights,
  offerName,
  offerPreheader,
  offerSaleUnit,
  offerSlug,
  offerStatus,
  offerSubjectLine,
  offerType,
  propertyId,
}: Props) {
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const [isTemplateModalVisible, setIsTemplateModalVisible] = useState(false);
  const [templateType, setTemplateType] = useState<TemplateType>('location');
  const [isGeneralSettingsExpanded, setIsGeneralSettingsExpanded] = useState(true);
  const [isOfferPageExpanded, setIsOfferPageExpanded] = useState(true);
  const [isMerchandisingEmailExpanded, setIsMerchandisingEmailExpanded] = useState(true);
  const [isSummaryBodyExpanded, setIsSummaryBodyExpanded] = useState(true);
  const [additionalDescriptionValue, setAdditionalDescriptionValue] = useState(offerAdditionalDescription ?? '');
  const [descriptionValue, setDescriptionValue] = useState(offerDescription ?? '');
  const [emailPreheaderValue, setEmailPreheaderValue] = useState(offerPreheader ?? '');
  const [emailSubjectLineValue, setEmailSubjectLineValue] = useState(offerSubjectLine ?? '');
  const [headlineValue, setHeadlineValue] = useState(offerName ?? '');
  const [saleUnitValue, setSaleUnitValue] = useState(offerSaleUnit ?? '');
  const { data: offerTemplates = [] } = useGetOfferTemplates();
  const { control, getValues, reset, setValue, watch } = useForm<SummaryBodyFormValues>({
    defaultValues: {
      facilities: offerFacilities ?? '',
      fine_print: offerFinePrint ?? '',
      getting_there: offerGettingThere ?? '',
      highlights: offerHighlights ?? '',
    },
  });
  const highlightsValue = watch('highlights') ?? '';
  const locationValue = watch('getting_there') ?? '';
  const typeValue = offerType ?? '';
  const typeLabel = offerType
    ? ((OFFER_FACING_NAMES as Record<string, string>)[offerType] ?? offerType)
    : 'Loading type';
  const statusValue = offerStatus ?? '';
  const slugValue = offerSlug ?? '';
  const shouldShowStatusFallback = !!statusValue && !OFFER_STATUS_OPTIONS.some((status) => status === statusValue);
  const filteredTemplates = useMemo(
    () =>
      offerTemplates.filter((template) => {
        const templateMatchesOfferType = template.offer_type === 'all' || template.offer_type === offerType;

        return template.template_type === templateType && templateMatchesOfferType;
      }),
    [offerTemplates, offerType, templateType],
  );
  const headlineAiContext = useMemo(
    () => ({
      model: 'offer',
      field: 'headline',
      propertyName: headlineValue,
    }),
    [headlineValue],
  );
  const descriptionAiContext = useMemo(
    () => ({
      model: 'offer',
      field: 'description',
      propertyName: headlineValue,
    }),
    [headlineValue],
  );
  const locationAiContext = useMemo(
    () => ({
      model: 'offer',
      field: 'location',
      propertyName: headlineValue,
    }),
    [headlineValue],
  );
  const updateHeadlineAiContext = useCallback(() => headlineAiContext, [headlineAiContext]);
  const updateDescriptionAiContext = useCallback(() => descriptionAiContext, [descriptionAiContext]);
  const updateLocationAiContext = useCallback(() => locationAiContext, [locationAiContext]);
  const hasHighlightsValue = useMemo(() => hasRichTextContent(highlightsValue), [highlightsValue]);
  const areAllSectionsExpanded =
    isGeneralSettingsExpanded && isOfferPageExpanded && isMerchandisingEmailExpanded && isSummaryBodyExpanded;
  const { mutate: syncFacilities, isPending: isSyncingFacilities } = useSyncPropertyFacilities({
    onSuccess: (facilities) => {
      setValue(
        'facilities',
        `${facilities.map((item) => `- ${item.name}`).join('\n')}\n\nSurcharges may apply to select facilities and services`,
      );
    },
  });

  useEffect(() => {
    setHeadlineValue(offerName ?? '');
    setSaleUnitValue(offerSaleUnit ?? '');
    setEmailSubjectLineValue(offerSubjectLine ?? '');
    setEmailPreheaderValue(offerPreheader ?? '');
    setDescriptionValue(offerDescription ?? '');
    setAdditionalDescriptionValue(offerAdditionalDescription ?? '');
  }, [offerAdditionalDescription, offerDescription, offerName, offerPreheader, offerSaleUnit, offerSubjectLine]);

  useEffect(() => {
    reset({
      facilities: offerFacilities ?? '',
      fine_print: offerFinePrint ?? '',
      getting_there: offerGettingThere ?? '',
      highlights: offerHighlights ?? '',
    });
  }, [offerFacilities, offerFinePrint, offerGettingThere, offerHighlights, reset]);

  const handleToggleAllSections = () => {
    const nextExpandedState = !areAllSectionsExpanded;

    setIsGeneralSettingsExpanded(nextExpandedState);
    setIsOfferPageExpanded(nextExpandedState);
    setIsMerchandisingEmailExpanded(nextExpandedState);
    setIsSummaryBodyExpanded(nextExpandedState);
  };

  const loadLocationTemplate = (template: App.OfferTemplate) => {
    const fieldName = template.template_type === 'fine_print' ? 'fine_print' : 'getting_there';

    setValue(fieldName, template.content);
    setIsTemplateModalVisible(false);
  };

  const appendLimitedTimeLuxFinePrint = () => {
    const currentFinePrintValue = getValues('fine_print');

    setValue('fine_print', `${currentFinePrintValue}\n\n${LUX_PLUS_FINEPRINT_CONTENT_FLASH}`);
  };

  const showTemplateModal = (type: TemplateType) => {
    setTemplateType(type);
    setIsTemplateModalVisible(true);
  };

  const handleSyncFacilities = () => {
    if (!propertyId) {
      return;
    }

    syncFacilities({ propertyId });
  };

  return (
    <Box>
      <OfferTemplateModal
        show={isTemplateModalVisible}
        hide={() => setIsTemplateModalVisible(false)}
        templates={filteredTemplates}
        templateType={templateType}
        loadTemplate={loadLocationTemplate}
      />

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4">Default content</Typography>
          <Typography variant="body1" color="text.secondary">
            Master copy used across the offer page, EDM, and merchandising surfaces.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button variant="outlined" size="small" startIcon={<UnfoldMoreIcon />} onClick={handleToggleAllSections}>
            {areAllSectionsExpanded ? 'Collapse all' : 'Expand all'}
          </Button>
          <Button variant="outlined" size="small" startIcon={<VisibilityIcon />}>
            Preview offer page
          </Button>
        </Stack>
      </Stack>

      <Accordion
        expanded={isGeneralSettingsExpanded}
        onChange={(_event, isExpanded) => setIsGeneralSettingsExpanded(isExpanded)}
        disableGutters
        variant="outlined"
        sx={ACCORDION_SX}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={ACCORDION_SUMMARY_SX}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <TuneIcon color="primary" />
            <Typography variant="h6">General settings</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={ACCORDION_DETAILS_SX}>
          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              maxWidth: 1280,
            }}
          >
            <FormControl fullWidth>
              <FormLabel sx={{ color: 'text.primary', fontWeight: 600, mb: 1 }}>Type</FormLabel>
              <Select disabled value={typeValue} onChange={noop} sx={{ bgcolor: 'background.paper' }}>
                <MenuItem value={typeValue}>{typeLabel}</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <FormLabel sx={{ color: 'text.primary', fontWeight: 600, mb: 1 }}>Status</FormLabel>
              <Select displayEmpty value={statusValue} onChange={noop} sx={{ bgcolor: 'background.paper' }}>
                {!statusValue && (
                  <MenuItem value="" disabled>
                    Loading status
                  </MenuItem>
                )}
                {OFFER_STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
                {shouldShowStatusFallback && <MenuItem value={statusValue}>{statusValue}</MenuItem>}
              </Select>
              <FormHelperText>Visible to customers once Rates & Inventory is also approved.</FormHelperText>
            </FormControl>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={isOfferPageExpanded}
        onChange={(_event, isExpanded) => setIsOfferPageExpanded(isExpanded)}
        disableGutters
        variant="outlined"
        sx={{ ...ACCORDION_SX, mt: 3 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={ACCORDION_SUMMARY_SX}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <ArticleIcon color="primary" />
            <Typography variant="h6">Offer page</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={ACCORDION_DETAILS_SX}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              required
              label="Headline"
              value={headlineValue}
              onChange={(event) => setHeadlineValue(event.target.value)}
              helperText={`${headlineValue.length} characters · ${HEADLINE_TARGET_COPY}`}
            />

            <AIGeneratorButton
              context={headlineAiContext}
              content={headlineValue}
              buttonLabel={headlineValue ? 'Rewrite Headline' : 'Generate Headline'}
              sx={AI_BUTTON_SX}
              updateContext={updateHeadlineAiContext}
              onChoose={setHeadlineValue}
            />

            <TextField fullWidth label="URL slug" value={slugValue} onChange={noop} />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={isMerchandisingEmailExpanded}
        onChange={(_event, isExpanded) => setIsMerchandisingEmailExpanded(isExpanded)}
        disableGutters
        variant="outlined"
        sx={{ ...ACCORDION_SX, mt: 3 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={ACCORDION_SUMMARY_SX}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <EmailIcon color="primary" />
            <Typography variant="h6">Merchandising & email</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={ACCORDION_DETAILS_SX}>
          <Stack spacing={3}>
            <Box
              sx={{
                display: 'grid',
                gap: 3,
                gridTemplateColumns: { xs: '1fr', md: 'minmax(240px, 1fr) minmax(0, 2fr)' },
              }}
            >
              <TextField
                fullWidth
                label="Sale unit"
                value={saleUnitValue}
                helperText={SALE_UNIT_HELPER_TEXT}
                slotProps={{ htmlInput: { maxLength: 6 } }}
                onChange={(event) => setSaleUnitValue(event.target.value)}
              />
              <TextField
                fullWidth
                label="Email subject line"
                value={emailSubjectLineValue}
                onChange={(event) => setEmailSubjectLineValue(event.target.value)}
              />
            </Box>

            <TextField
              fullWidth
              label="Email preheader"
              value={emailPreheaderValue}
              onChange={(event) => setEmailPreheaderValue(event.target.value)}
            />

            <Stack spacing={1}>
              <FormLabel>Description</FormLabel>
              <Box sx={{ '& .rdw-editor-main': { minHeight: 260 } }}>
                <MarkdownEditor
                  value={descriptionValue}
                  wrapperId="offer-builder-default-content-description"
                  onChange={setDescriptionValue}
                  onBlur={setDescriptionValue}
                />
              </Box>
              <AIGeneratorButton
                context={descriptionAiContext}
                content={descriptionValue.trim() ? descriptionValue : undefined}
                buttonLabel={descriptionValue.trim() ? 'Rewrite description' : 'Generate content'}
                updateContext={updateDescriptionAiContext}
                onChoose={setDescriptionValue}
              />
            </Stack>

            <Stack spacing={1}>
              <FormLabel>Additional description</FormLabel>
              <Box sx={{ '& .rdw-editor-main': { minHeight: 180 } }}>
                <MarkdownEditor
                  value={additionalDescriptionValue}
                  placeholder={ADDITIONAL_DESCRIPTION_PLACEHOLDER}
                  wrapperId="offer-builder-default-content-additional-description"
                  onChange={setAdditionalDescriptionValue}
                  onBlur={setAdditionalDescriptionValue}
                />
              </Box>
              <FormHelperText sx={{ ml: 0 }}>{ADDITIONAL_DESCRIPTION_HELPER_TEXT}</FormHelperText>
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={isSummaryBodyExpanded}
        onChange={(_event, isExpanded) => setIsSummaryBodyExpanded(isExpanded)}
        disableGutters
        variant="outlined"
        sx={{ ...ACCORDION_SX, mt: 3 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={ACCORDION_SUMMARY_SX}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <SubjectIcon color="primary" />
            <Typography variant="h6">Summary & body</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={ACCORDION_DETAILS_SX}>
          <Stack spacing={3}>
            <Box
              sx={{
                '& .rdw-editor-main': {
                  minHeight: hasHighlightsValue
                    ? POPULATED_HIGHLIGHTS_EDITOR_MIN_HEIGHT
                    : EMPTY_HIGHLIGHTS_EDITOR_MIN_HEIGHT,
                },
              }}
            >
              <RichTextEditor control={control} label="Highlights" name="highlights" />
            </Box>

            <Stack spacing={1}>
              <Box sx={{ '& .rdw-editor-main': { minHeight: 180 } }}>
                <RichTextEditor control={control} label="Locations" name="getting_there" canUseBrandTokens />
              </Box>
              <Stack
                spacing={1.5}
                alignItems="flex-start"
                sx={{
                  '& > .MuiButton-root': {
                    minHeight: 36,
                    width: { xs: '100%', sm: 'auto' },
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                <Button variant="outlined" startIcon={<BookmarksIcon />} onClick={() => showTemplateModal('location')}>
                  Show templates
                </Button>
                <AIGeneratorButton
                  context={locationAiContext}
                  content={locationValue.trim() ? locationValue : undefined}
                  buttonLabel={locationValue.trim() ? 'Rewrite location' : 'Generate location'}
                  sx={AI_BUTTON_SX}
                  updateContext={updateLocationAiContext}
                  onChoose={(value) => setValue('getting_there', value)}
                />
              </Stack>
            </Stack>

            <Stack spacing={1.5}>
              <Box sx={{ '& .rdw-editor-main': { minHeight: 180 } }}>
                <RichTextEditor control={control} label="Fine print" name="fine_print" canUseBrandTokens />
              </Box>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', md: 'center' }}
                sx={{
                  '& .MuiButton-root': {
                    minHeight: 36,
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<BookmarksIcon />}
                  onClick={() => showTemplateModal('fine_print')}
                >
                  Show templates
                </Button>
                <Button variant="outlined" startIcon={<FinePrintIcon />} onClick={appendLimitedTimeLuxFinePrint}>
                  Append Lux Plus Fine Print
                </Button>
                <Button component={Link} to={`/edit-offers/${offerId}/structured-fine-print`} variant="outlined">
                  Structured Fine Print
                </Button>
              </Stack>
            </Stack>

            <Stack spacing={1.5}>
              <Box sx={{ '& .rdw-editor-main': { minHeight: 180 } }}>
                <RichTextEditor control={control} label="Facilities" name="facilities" />
              </Box>
              <Button
                disabled={!propertyId || isSyncingFacilities}
                variant="outlined"
                startIcon={<SyncIcon />}
                onClick={handleSyncFacilities}
                sx={{
                  alignSelf: 'flex-start',
                  minHeight: 36,
                  width: { xs: '100%', sm: 'auto' },
                  whiteSpace: 'nowrap',
                }}
              >
                {isSyncingFacilities ? 'Syncing...' : 'Sync facilities'}
              </Button>
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
